import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmModal from './ConfirmModal';
import PasswordInputField from './PasswordInputField';
import './TrainerDashboard.css';

const API_URL = '/api';

function TrainerDashboard({ auth }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    email: '',
    phone: '',
    password: ''
  });

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_URL}/trainer/clients`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setClients(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching clients');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedClient(null);
    setFormData({ name: '', birth_date: '', email: '', phone: '', password: '' });
    setShowModal(true);
    setSuccessMsg('');
    setError(null);
  };

  const handleOpenEditModal = (e, client) => {
    e.stopPropagation();
    setModalMode('edit');
    setSelectedClient(client);
    setFormData({
      name: client.name || '',
      birth_date: client.birth_date || client.birthDate || '',
      email: client.email || '',
      phone: client.phone || '',
      password: ''
    });
    setShowModal(true);
    setSuccessMsg('');
    setError(null);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setError('Name, email and phone number are required');
      return;
    }

    try {
      if (modalMode === 'create') {
        await axios.post(`${API_URL}/trainer/clients`, formData, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        setSuccessMsg(`Client "${formData.name}" added successfully.`);
      } else {
        const payload = {
          name: formData.name,
          birth_date: formData.birth_date,
          email: formData.email,
          phone: formData.phone
        };
        if (formData.password.trim()) {
          payload.password = formData.password;
        }
        await axios.put(`${API_URL}/trainer/clients/${selectedClient.id}`, payload, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        setSuccessMsg(`Client "${formData.name}" updated successfully.`);
      }

      setShowModal(false);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred');
    }
  };

  const handleDeleteClick = (e, client) => {
    e.stopPropagation();
    setClientToDelete(client);
  };

  const handleConfirmDeleteClient = async () => {
    if (!clientToDelete) return;
    try {
      setError(null);
      await axios.delete(`${API_URL}/trainer/clients/${clientToDelete.id}`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setSuccessMsg(`Client "${clientToDelete.name}" has been deleted.`);
      setClientToDelete(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting client');
      setClientToDelete(null);
    }
  };

  const handleCancelDeleteClient = () => {
    setClientToDelete(null);
  };

  return (
    <div className="trainer-dashboard-container">
      {error && <div className="error">{error}</div>}
      {successMsg && <div className="success-banner">{successMsg}</div>}

      {/* Clients Grid */}
      <div className="clients-section-card">
        <div className="clients-section-header">
          <div className="trainer-nav-tabs">
            <button
              type="button"
              className="trainer-nav-tab active"
            >
              Clients
            </button>
            <button
              type="button"
              className="trainer-nav-tab"
              onClick={() => navigate('/trainer/templates')}
            >
              Templates
            </button>
          </div>

          <button
            className="button button-primary btn-add-client"
            onClick={handleOpenCreateModal}
          >
            New Client
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading clients...</div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            No clients assigned yet. Click <span>New Client</span> to create an account.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="trainer-clients-table">
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id} className="trainer-client-row">
                    <td className="cell-client-name">
                      <button
                        type="button"
                        className="btn-client-link"
                        onClick={() => navigate(`/trainer/client/${client.id}`)}
                        title="Open calendar for this client"
                      >
                        {client.name}
                      </button>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="client-table-actions">
                        <button
                          type="button"
                          className="button button-secondary btn-sm btn-action-edit"
                          onClick={(e) => handleOpenEditModal(e, client)}
                          title="Edit client"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="button button-danger btn-sm btn-action-delete"
                          onClick={(e) => handleDeleteClick(e, client)}
                          title="Delete client"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for creating / editing client */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3 className="modal-title">
                {modalMode === 'create' ? 'Add New Client' : 'Edit Client Details'}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmitForm}>
              <div className="form-group">
                <label htmlFor="client-name">Full Name *</label>
                <input
                  id="client-name"
                  type="text"
                  maxLength={100}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="client-birth-date">Date of Birth</label>
                <input
                  id="client-birth-date"
                  type="text"
                  maxLength={12}
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                  placeholder="dd.mm.yyyy"
                />
              </div>

              <div className="form-group">
                <label htmlFor="client-email">Email Address *</label>
                <input
                  id="client-email"
                  type="email"
                  maxLength={120}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="client-phone">Phone Number *</label>
                <input
                  id="client-phone"
                  type="tel"
                  maxLength={30}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+381..."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="client-password">
                  {modalMode === 'create' ? 'Password' : 'New Password (optional)'}
                </label>
                <PasswordInputField
                  id="client-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={modalMode === 'create' ? 'default: client123!' : 'Leave empty to keep current.'}
                  maxLength={128}
                />
              </div>

              <div className="modal-buttons" style={{ marginTop: '20px' }}>
                <button type="submit" className="button button-primary">
                  {modalMode === 'create' ? 'Add Client' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={Boolean(clientToDelete)}
        message={clientToDelete ? `Are you sure you want to delete client ${clientToDelete.name}?` : ''}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleConfirmDeleteClient}
        onCancel={handleCancelDeleteClient}
      />
    </div>
  );
}

export default TrainerDashboard;
